import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import useApiRequest from '@/lib/hooks/use-request';

interface RealTimeContextType {
  isConnected: boolean;
  lastDealId: string | null;
}

const RealTimeContext = createContext<RealTimeContextType>({
  isConnected: false,
  lastDealId: null,
});

export const useRealTime = () => useContext(RealTimeContext);

interface RealTimeProviderProps {
  children: React.ReactNode;
}

export const RealTimeProvider: React.FC<RealTimeProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(true); // For now, always show as connected
  const [lastDealId, setLastDealId] = useState<string | null>(null);
  const [isFirstCheck, setIsFirstCheck] = useState(true);
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { apiRequest } = useApiRequest();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio('/notification.mp3');
    audioRef.current.volume = 0.5;
  }, []);

  // Play notification sound using Web Audio API
  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Web Audio API failed:', e);
    }
  };

  // Poll for new deals every 5 seconds
  const checkForNewDeals = useCallback(async () => {
    try {
      // Get deals from initial endpoint
      const response = await apiRequest<any>('deals/initial', 'GET', { 
        suppressGlobalError: true,
        test: false 
      });
      
      // Extract all deals from all stages
      const allDeals: any[] = [];
      if (response?.data?.stages) {
        response.data.stages.forEach((stage: any) => {
          if (stage.deals && Array.isArray(stage.deals)) {
            allDeals.push(...stage.deals);
          }
        });
      }
      
      // Sort by creation date to get the latest
      allDeals.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      if (allDeals.length > 0) {
        const latestDeal = allDeals[0];
        
        // On first check, just store the latest deal ID without notification
        if (isFirstCheck) {
          setLastDealId(latestDeal._id);
          setIsFirstCheck(false);
          console.log('[RealTime] First check - storing latest deal ID:', latestDeal._id);
          return;
        }
        
        // Check if this is a new deal we haven't seen
        if (latestDeal._id !== lastDealId) {
          // New deal detected!
          console.log('[RealTime] New deal detected!', latestDeal.title);
          const dealAge = new Date().getTime() - new Date(latestDeal.createdAt).getTime();
          
          // Only show notification if deal was created in the last 60 seconds
          if (dealAge < 60000) {
            // Play sound using Web Audio API as fallback
            try {
              if (audioRef.current) {
                audioRef.current.play().catch(e => {
                  console.log('Audio file play failed, using Web Audio API:', e);
                  playNotificationSound();
                });
              } else {
                playNotificationSound();
              }
            } catch (e) {
              console.log('Sound failed:', e);
            }

            // Show toast notification
            toast.success(
              <div 
                className="cursor-pointer w-full" 
                onClick={() => {
                  navigate(`/deals/${latestDeal._id}`);
                }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Bell className="h-4 w-4" />
                  <span className="font-semibold">Neuer Lead!</span>
                </div>
                <div className="text-sm font-medium">{latestDeal.title}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    <div>{latestDeal.summary?.personSummary?.title || 'Unbekannt'}</div>
                    {latestDeal.summary?.personSummary?.title && <div>📞 Kontakt verfügbar</div>}
                    <div>📍 PLZ: Details im Deal</div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Zugewiesen an: {latestDeal.belongsToUserFullName || 'Unbekannt'}
                  </div>
              </div>,
              {
                duration: 10000,
                action: {
                  label: 'Anzeigen',
                  onClick: () => navigate(`/deals/${latestDeal._id}`),
                },
              }
            );

            // Browser notification
            if ('Notification' in window && Notification.permission === 'granted') {
              const notification = new Notification('Neuer Lead!', {
                body: `${latestDeal.title} - ${latestDeal.summary?.personSummary?.title || 'Neuer Kontakt'}`,
                icon: '/logo192.png',
                tag: latestDeal._id,
                requireInteraction: true,
              });

              notification.onclick = () => {
                window.focus();
                navigate(`/deals/${latestDeal._id}`);
                notification.close();
              };
            }

            // Trigger refresh event
            window.dispatchEvent(new CustomEvent('deal-updated', { detail: { dealId: latestDeal._id } }));
          }
        }
        
        // Update last deal ID
        setLastDealId(latestDeal._id);
      }
    } catch (error) {
      console.log('[RealTime] Error checking for new deals:', error);
    }
  }, [lastDealId, apiRequest, navigate, isFirstCheck]);

  // Start polling when component mounts
  useEffect(() => {
    // Initial check
    checkForNewDeals();

    // Poll every 5 seconds
    intervalRef.current = setInterval(checkForNewDeals, 5000);

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkForNewDeals]);

  return (
    <RealTimeContext.Provider value={{ isConnected, lastDealId }}>
      {children}
    </RealTimeContext.Provider>
  );
};
